import React, { useState } from 'react';
import { Container, Nav, NavDropdown, Navbar, Offcanvas } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom';
import { GROUP } from '../CompanyInfo';
import './AppNav.css';

/**
 * Primary nav for the authenticated areas: an inline bar from `lg` up, and a
 * real Offcanvas sidebar below it.
 *
 * Replaces the hover-driven strip this used to be - that opened its submenus
 * on mouseenter, which never fires on a touch screen, so the Reports and User
 * menus were unreachable on a phone. It also rendered <li> inside <Link>
 * inside a <div> and kept the submenus aligned with a translateX equal to the
 * strip's horizontal scroll.
 *
 *   items    [{ label, to }]                  flat links
 *   menus    [{ label, items:[{label,to}] }]  grouped: dropdown on desktop,
 *                                             a labelled block in the sidebar
 *   itemsAfter [{ label, to }]               flat links placed after the menus
 *   trailing React node, right-aligned (e.g. the log-out button)
 */
const AppNav = ({ items = [], menus = [], itemsAfter = [], trailing = null }) => {
    const [open, setOpen] = useState(false);
    const history = useHistory();
    const { pathname } = useLocation();

    const go = (to) => {
        setOpen(false);
        history.push(to);
    };

    const isActive = (to) => pathname === to;
    const groupActive = (group) => group.items.some((i) => isActive(i.to));

    // On a phone the bar holds nothing but the toggler, which read as a tall
    // empty band. It now carries the name of the page you are on.
    const currentLabel = (() => {
        const flat = [...items, ...itemsAfter].find((i) => isActive(i.to));
        if (flat) return flat.label;
        for (let g = 0; g < menus.length; g++) {
            const hit = menus[g].items.find((i) => isActive(i.to));
            if (hit) return hit.label;
        }
        return 'Menu';
    })();

    return (
        <Navbar
            expand="lg"
            className='appNav'
            expanded={open}
            onToggle={setOpen}
        >
            <Container fluid className='appNavInner'>
                <Navbar.Toggle aria-controls="appNavCollapse" aria-label="Open menu" />

                <span className='appNavCurrent d-lg-none'>{currentLabel}</span>

                <Navbar.Offcanvas
                    id="appNavCollapse"
                    aria-labelledby="appNavLabel"
                    placement="start"
                    className='appDrawer'
                    onHide={() => setOpen(false)}
                >
                    <Offcanvas.Header closeButton closeVariant='white'>
                        <Offcanvas.Title id="appNavLabel" className='appDrawerTitle'>
                            <img src={GROUP.logo} className='appDrawerLogo' alt="" />
                            {GROUP.name}
                        </Offcanvas.Title>
                    </Offcanvas.Header>

                    <Offcanvas.Body>
                        <Nav className='appNavLinks me-auto'>
                            {items.map((item) => (
                                <Nav.Link
                                    key={item.to}
                                    className={isActive(item.to) ? 'appNavActive' : ''}
                                    onClick={() => go(item.to)}
                                >
                                    {item.label}
                                </Nav.Link>
                            ))}

                            {/* Desktop: a dropdown per group. */}
                            {menus.map((group) => (
                                <NavDropdown
                                    key={group.label}
                                    title={group.label}
                                    id={'appNavMenu-' + group.label}
                                    className={
                                        'appNavMenu d-none d-lg-block' +
                                        (groupActive(group) ? ' appNavActive' : '')
                                    }
                                >
                                    {group.items.map((i) => (
                                        <NavDropdown.Item
                                            key={i.to}
                                            active={isActive(i.to)}
                                            onClick={() => go(i.to)}
                                        >
                                            {i.label}
                                        </NavDropdown.Item>
                                    ))}
                                </NavDropdown>
                            ))}
                            {itemsAfter.map((item) => (
                                <Nav.Link
                                    key={item.to}
                                    className={'d-none d-lg-block' + (isActive(item.to) ? ' appNavActive' : '')}
                                    onClick={() => go(item.to)}
                                >
                                    {item.label}
                                </Nav.Link>
                            ))}
                        </Nav>

                        {/* Sidebar: the same groups as labelled blocks, because a
                            dropdown nested in a drawer is awkward to tap. */}
                        {menus.map((group) => (
                            <Nav
                                key={group.label}
                                className='appNavGroup d-lg-none'
                            >
                                <div className='appNavGroupLabel'>{group.label}</div>
                                {group.items.map((i) => (
                                    <Nav.Link
                                        key={i.to}
                                        className={isActive(i.to) ? 'appNavActive' : ''}
                                        onClick={() => go(i.to)}
                                    >
                                        {i.label}
                                    </Nav.Link>
                                ))}
                            </Nav>
                        ))}

                        {itemsAfter.length > 0 && (
                            <Nav className='appNavGroup d-lg-none'>
                                {itemsAfter.map((item) => (
                                    <Nav.Link
                                        key={item.to}
                                        className={isActive(item.to) ? 'appNavActive' : ''}
                                        onClick={() => go(item.to)}
                                    >
                                        {item.label}
                                    </Nav.Link>
                                ))}
                            </Nav>
                        )}

                        {trailing ? (
                            <div className='appNavTrailing'>{trailing}</div>
                        ) : null}
                    </Offcanvas.Body>
                </Navbar.Offcanvas>
            </Container>
        </Navbar>
    );
};

export default AppNav;
